import { formatDollars } from '@fruitful/fruitils';
import { scaleLinear } from 'd3-scale';
import { area, curveMonotoneX, curveStepAfter, line } from 'd3-shape';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { type LayoutChangeEvent, View as RNView } from 'react-native';
import Svg, {
    Circle,
    Line,
    LinearGradient,
    Path,
    Rect,
    Stop,
} from 'react-native-svg';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
    type ColorTokenKey,
    LabelRegular,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { ApexPerformanceDatapointDto } from '../types/BridgeftTypes';

const ACCOUNT_VALUE_LINE_COLOR: ColorTokenKey = '$green1100';
const NET_CONTRIBUTION_LINE_COLOR: ColorTokenKey = '$green900';
const MARKER_LINE_COLOR: ColorTokenKey = '$gray1200';
const GRADIENT_START_COLOR: ColorTokenKey = '$green400';

const STROKE_WIDTH = 2;
const CURVED_MARKER_RADIUS = 5;
const STEPPED_MARKER_SIZE = CURVED_MARKER_RADIUS * 2;
const STEPPED_MARKER_RX = Math.min(2, STEPPED_MARKER_SIZE / 2);

const CHART_HORIZONTAL_OUTSET = Math.ceil(
    Math.max(
        CURVED_MARKER_RADIUS + STROKE_WIDTH,
        STEPPED_MARKER_SIZE / 2 + STROKE_WIDTH,
    ) + 2,
);

const GRAPH_HEIGHT = 225;
const GRAPH_MARGIN = 0;
const GRAPH_VERTICAL_MARGIN = 12;

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const readNumericField = (
    nativeEvent: object,
    key: 'offsetX' | 'locationX',
): number | null => {
    if (
        key in nativeEvent &&
        typeof (nativeEvent as Record<string, unknown>)[key] === 'number' &&
        !Number.isNaN((nativeEvent as Record<string, number>)[key])
    ) {
        return (nativeEvent as Record<string, number>)[key] as number;
    }
    return null;
};

const getPointerOffsetX = (nativeEvent: object): number | null =>
    readNumericField(nativeEvent, 'offsetX') ??
    readNumericField(nativeEvent, 'locationX');

const AccountValueMarker = ({ x, y }: { x: number; y: number }) => {
    const { getColor } = useThemeColors();
    const white = getColor('$white');
    const primary = getColor(ACCOUNT_VALUE_LINE_COLOR);

    return (
        <Circle
            cx={x}
            cy={y}
            fill={white}
            r={CURVED_MARKER_RADIUS}
            stroke={primary}
            strokeWidth={STROKE_WIDTH}
            pointerEvents="none"
        />
    );
};

const NetContributionMarker = ({ x, y }: { x: number; y: number }) => {
    const { getColor } = useThemeColors();
    const white = getColor('$white');
    const secondary = getColor(NET_CONTRIBUTION_LINE_COLOR);

    return (
        <Rect
            fill={white}
            height={STEPPED_MARKER_SIZE}
            rx={STEPPED_MARKER_RX}
            ry={STEPPED_MARKER_RX}
            stroke={secondary}
            strokeWidth={STROKE_WIDTH}
            width={STEPPED_MARKER_SIZE}
            x={x}
            y={y}
            pointerEvents="none"
        />
    );
};

const PerformanceLegendIcon = ({
    variant,
}: {
    variant: 'accountValuation' | 'netContribution';
}) => {
    const LEGEND_ICON_WIDTH = 24;
    const LEGEND_ICON_HEIGHT = 18;
    const LEGEND_CENTER_X = LEGEND_ICON_WIDTH / 2;
    const LEGEND_CENTER_Y = LEGEND_ICON_HEIGHT / 2;
    const LEGEND_LINE_HALF_LEN = 12;

    const isAccountValuation = variant === 'accountValuation';

    const { getColor } = useThemeColors();
    const strokeColor = getColor(
        isAccountValuation
            ? ACCOUNT_VALUE_LINE_COLOR
            : NET_CONTRIBUTION_LINE_COLOR,
    );

    return (
        <Svg
            height={LEGEND_ICON_HEIGHT}
            viewBox={`0 0 ${LEGEND_ICON_WIDTH} ${LEGEND_ICON_HEIGHT}`}
            width={LEGEND_ICON_WIDTH}
        >
            <Line
                stroke={strokeColor}
                strokeDasharray={isAccountValuation ? '0 0' : '3 4'}
                strokeLinecap="round"
                strokeWidth={2}
                x1={LEGEND_CENTER_X - LEGEND_LINE_HALF_LEN}
                x2={LEGEND_CENTER_X + LEGEND_LINE_HALF_LEN}
                y1={LEGEND_CENTER_Y}
                y2={LEGEND_CENTER_Y}
            />
            {isAccountValuation ? (
                <AccountValueMarker x={LEGEND_CENTER_X} y={LEGEND_CENTER_Y} />
            ) : (
                <NetContributionMarker
                    x={LEGEND_CENTER_X - STEPPED_MARKER_SIZE / 2}
                    y={LEGEND_CENTER_Y - STEPPED_MARKER_SIZE / 2}
                />
            )}
        </Svg>
    );
};

const SnapshotRow = ({
    datapoint,
}: {
    datapoint: ApexPerformanceDatapointDto;
}) => (
    <YStack gap={8}>
        <LabelRegular color="$gray1000">{datapoint.step}</LabelRegular>
        <XStack justifyContent="space-between" alignItems="center">
            <YStack>
                <XStack alignItems="center" gap={6}>
                    <PerformanceLegendIcon variant="accountValuation" />
                    <BodySmallRegular>Account value</BodySmallRegular>
                </XStack>
                <BodyLargeEmphasized>
                    {formatDollars(datapoint.accountValuation)}
                </BodyLargeEmphasized>
            </YStack>
            <YStack alignItems="flex-end">
                <XStack alignItems="center" gap={6}>
                    <PerformanceLegendIcon variant="netContribution" />
                    <BodySmallRegular>Net contributions</BodySmallRegular>
                </XStack>
                <BodyLargeEmphasized>
                    {formatDollars(datapoint.netContribution)}
                </BodyLargeEmphasized>
            </YStack>
        </XStack>
    </YStack>
);

export const BridgeftPerformanceChart = ({
    datapoints,
}: {
    datapoints: ApexPerformanceDatapointDto[];
}) => {
    const curvedAreaGradientId = `bridgeftPerfArea-${useId().replace(/:/g, '')}`;

    const [containerWidth, setContainerWidth] = useState(0);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const lastScrubIndexRef = useRef<number | null>(null);

    const { getColor } = useThemeColors();

    const areaGradientStartColor = getColor(GRADIENT_START_COLOR);
    const areaGradientEndColor = getColor('$white');
    const primaryLineColor = getColor(ACCOUNT_VALUE_LINE_COLOR);
    const secondaryLineColor = getColor(NET_CONTRIBUTION_LINE_COLOR);
    const crosshairColor = getColor(MARKER_LINE_COLOR);

    useEffect(() => {
        lastScrubIndexRef.current = null;
        setActiveIndex(null);
    }, [datapoints]);

    const onContainerLayout = useCallback((event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0) {
            setContainerWidth(width);
        }
    }, []);

    const chartWidth = Math.max(containerWidth, 1);

    const { steppedSeries, curvedSeries } = useMemo(
        () => ({
            steppedSeries: datapoints.map(
                (p, i) => [i, p.netContribution] as [number, number],
            ),
            curvedSeries: datapoints.map(
                (p, i) => [i, p.accountValuation] as [number, number],
            ),
        }),
        [datapoints],
    );

    const {
        steppedPathD,
        curvedAreaD,
        curvedLineD,
        xScale,
        yScale,
        plotTop,
        plotBottom,
        pointCount,
    } = useMemo(() => {
        const innerWidth = chartWidth - 2 * GRAPH_MARGIN;
        const innerHeight = GRAPH_HEIGHT - 2 * GRAPH_VERTICAL_MARGIN;
        const plotTopY = GRAPH_VERTICAL_MARGIN;
        const plotBottomY = GRAPH_VERTICAL_MARGIN + innerHeight;

        if (steppedSeries.length === 0) {
            return {
                steppedPathD: '',
                curvedAreaD: '',
                curvedLineD: '',
                xScale: scaleLinear().domain([0, 1]).range([0, 0]),
                yScale: scaleLinear().domain([0, 1]).range([0, 0]),
                plotTop: plotTopY,
                plotBottom: plotBottomY,
                pointCount: 0,
            };
        }

        const ys = [
            ...steppedSeries.map((d) => d[1]),
            ...curvedSeries.map((d) => d[1]),
        ];
        const xMin = 0;
        const xMax = steppedSeries.length - 1;
        const yMin = Math.min(0, ...ys);
        const yMax = Math.max(1, ...ys);

        const xScale0 = scaleLinear()
            .domain([xMin, xMax])
            .range([
                GRAPH_MARGIN + CHART_HORIZONTAL_OUTSET,
                GRAPH_MARGIN + innerWidth + CHART_HORIZONTAL_OUTSET,
            ]);

        const yScale0 = scaleLinear()
            .domain([yMin, yMax])
            .range([plotBottomY, plotTopY]);

        const baselineY = plotBottomY;

        const steppedLineGenerator = line<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y((d) => yScale0(d[1]) ?? 0)
            .curve(curveStepAfter);

        const curvedLineGenerator = line<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y((d) => yScale0(d[1]) ?? 0)
            .curve(curveMonotoneX);

        const curvedAreaGenerator = area<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y0(baselineY)
            .y1((d) => yScale0(d[1]) ?? 0)
            .curve(curveMonotoneX);

        return {
            steppedPathD: steppedLineGenerator(steppedSeries) ?? '',
            curvedAreaD: curvedAreaGenerator(curvedSeries) ?? '',
            curvedLineD: curvedLineGenerator(curvedSeries) ?? '',
            xScale: xScale0,
            yScale: yScale0,
            plotTop: plotTopY,
            plotBottom: plotBottomY,
            pointCount: steppedSeries.length,
        };
    }, [chartWidth, steppedSeries, curvedSeries]);

    const nearestIndexFromOffsetX = useCallback(
        (offsetX: number) => {
            if (pointCount < 1) return null;
            if (pointCount === 1) return 0;
            const innerWidth = chartWidth - 2 * GRAPH_MARGIN;
            if (innerWidth <= 0) return null;
            const rel = clamp((offsetX - GRAPH_MARGIN) / innerWidth, 0, 1);
            return Math.round(rel * (pointCount - 1));
        },
        [chartWidth, pointCount],
    );

    const updatePointer = useCallback(
        (nativeEvent: object) => {
            const offsetX = getPointerOffsetX(nativeEvent);
            if (offsetX == null) return;
            const nextIndex = nearestIndexFromOffsetX(offsetX);
            lastScrubIndexRef.current = nextIndex;
            setActiveIndex(nextIndex);
        },
        [nearestIndexFromOffsetX],
    );

    const clearPointer = useCallback(() => {
        lastScrubIndexRef.current = null;
        setActiveIndex(null);
    }, []);

    const crosshairX =
        activeIndex != null && pointCount > 0 ? xScale(activeIndex) : null;

    const activeDatum =
        activeIndex != null ? datapoints[activeIndex] : undefined;
    const curvedIntersectionY =
        crosshairX != null && activeDatum != null
            ? yScale(activeDatum.accountValuation)
            : null;
    const steppedIntersectionY =
        crosshairX != null && activeDatum != null
            ? yScale(activeDatum.netContribution)
            : null;

    const svgWidth = chartWidth + 2 * CHART_HORIZONTAL_OUTSET;

    if (datapoints.length === 0) return null;

    return (
        <YStack gap={12}>
            {activeDatum != null && <SnapshotRow datapoint={activeDatum} />}
            {activeDatum == null && (
                <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center" gap={6}>
                        <PerformanceLegendIcon variant="accountValuation" />
                        <BodySmallRegular>Account value</BodySmallRegular>
                    </XStack>
                    <XStack alignItems="center" gap={6}>
                        <PerformanceLegendIcon variant="netContribution" />
                        <BodySmallRegular>Net contributions</BodySmallRegular>
                    </XStack>
                </XStack>
            )}
            <View
                alignSelf="stretch"
                height={GRAPH_HEIGHT}
                overflow="visible"
                position="relative"
                width="100%"
                onLayout={onContainerLayout}
            >
                <RNView
                    pointerEvents="none"
                    style={{
                        height: GRAPH_HEIGHT,
                        left: -CHART_HORIZONTAL_OUTSET,
                        overflow: 'visible',
                        position: 'absolute',
                        top: 0,
                        width: svgWidth,
                    }}
                >
                    <Svg
                        height={GRAPH_HEIGHT}
                        viewBox={`0 0 ${svgWidth} ${GRAPH_HEIGHT}`}
                        width={svgWidth}
                    >
                        <LinearGradient
                            id={curvedAreaGradientId}
                            x1="0%"
                            y1="0%"
                            x2="0%"
                            y2="100%"
                            gradientUnits="objectBoundingBox"
                        >
                            <Stop
                                offset="0"
                                stopColor={areaGradientStartColor}
                            />
                            <Stop offset="1" stopColor={areaGradientEndColor} />
                        </LinearGradient>
                        <Path
                            d={curvedAreaD}
                            fill={`url(#${curvedAreaGradientId})`}
                            stroke="none"
                        />
                        <Path
                            d={curvedLineD}
                            fill="none"
                            stroke={primaryLineColor}
                            strokeWidth={STROKE_WIDTH}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <Path
                            d={steppedPathD}
                            fill="none"
                            stroke={secondaryLineColor}
                            strokeWidth={STROKE_WIDTH}
                            strokeDasharray="4 4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {crosshairX != null && (
                            <Line
                                x1={crosshairX}
                                x2={crosshairX}
                                y1={plotTop}
                                y2={plotBottom}
                                stroke={crosshairColor}
                                strokeOpacity={0.5}
                                strokeWidth={1}
                                strokeDasharray="2 2"
                                pointerEvents="none"
                            />
                        )}
                        {crosshairX != null && curvedIntersectionY != null && (
                            <AccountValueMarker
                                x={crosshairX}
                                y={curvedIntersectionY}
                            />
                        )}
                        {crosshairX != null && steppedIntersectionY != null && (
                            <NetContributionMarker
                                x={crosshairX - STEPPED_MARKER_SIZE / 2}
                                y={
                                    steppedIntersectionY -
                                    STEPPED_MARKER_SIZE / 2
                                }
                            />
                        )}
                    </Svg>
                </RNView>
                <RNView
                    collapsable={false}
                    // @ts-expect-error cursor is web-only
                    style={{
                        cursor: 'crosshair',
                        height: GRAPH_HEIGHT,
                        left: 0,
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        zIndex: 1,
                    }}
                    onPointerDown={(e) => {
                        updatePointer(e.nativeEvent);
                    }}
                    onPointerLeave={clearPointer}
                    onPointerMove={(e) => {
                        updatePointer(e.nativeEvent);
                    }}
                />
            </View>
        </YStack>
    );
};
